import { useCallback, useMemo, useState, useEffect } from 'react';
import { ChartSection, ChartHeader } from '@instamenu/ui-system-next';
import { PagedConnectionTable, reactionColumn } from '@/components/core/tables';
import {
  dateFormats,
  formatDate,
  formatPhone,
  formatTimeRange,
  formatTimespan,
} from '@instamenu/formatters';
import { useSalesSummaryContext } from '../../SalesSummaryContext';
import {
  StatsOrdersDocument,
  StatsOrdersQuery,
  StatsOrdersQueryVariables,
} from './queries.generated';
import { LinkNext } from '@instamenu/ui-system-next';
import { refundStyle } from './Orders.css';
import { uniqBy } from 'lodash-es';
import { Maybe, OrderExportFormatEnum, SortDirection } from '@instamenu/data-access-stats';
import { apiClient } from '@instamenu/context-api';
import { ExportDataButton } from '@/components/core/forms/ExportDataButton';
import {
  CreateOrderExportDocument,
  CreateOrderExportMutation,
  CreateOrderExportMutationVariables,
} from './queries.generated';
import { CustomI18n, useLocale } from '@instamenu/context-i18n';
import { SortingState, createColumnHelper } from '@tanstack/react-table';
import { columnHelpers } from '@/components/core/tables/column-helpers';

export type OrderNode = NonNullable<
  NonNullable<StatsOrdersQuery['stats']['ordersConnection']['nodes']>[0]
>;

const columnHelper = createColumnHelper<OrderNode>();

// Wire-level format values. Kept as a `const` union so a stringly-typed
// regression (e.g., a third format silently falling back to CSV) becomes
// a compile error, not a silent behavior change. Module-scoped so they
// aren't re-allocated on every render of `Orders`.
const EXPORT_FORMAT_VALUES = ['CSV', 'QBO'] as const;
type ExportFormat = (typeof EXPORT_FORMAT_VALUES)[number];

const FORMAT_ENUM_MAP: Record<ExportFormat, OrderExportFormatEnum> = {
  CSV: OrderExportFormatEnum.Csv,
  QBO: OrderExportFormatEnum.Qbo,
};

const isExportFormat = (v: string | undefined): v is ExportFormat =>
  v !== undefined && (EXPORT_FORMAT_VALUES as readonly string[]).includes(v);

export default function Orders() {
  const { statsQueryVariables, timespan, times } = useSalesSummaryContext();
  const { i18n, storedLocale } = useLocale();
  const [loading, setLoading] = useState(false);
  const [orderBy, setOrderBy] = useState<SortingState>([]);
  const [connectionData, setConnectionData] =
    useState<StatsOrdersQuery['stats']['ordersConnection']>();
  const pageSize = 10;
  const [pageIndex, setPageIndex] = useState(0);

  const sortTableColumns = (sortBy: SortingState, i18n: CustomI18n) => {
    const columnIdMap: Record<string, string> = {
      restaurant_name: 'restaurantName',
      number: 'sequence',
      tip: 'serviceFee',
      'restaurant.name': 'restaurantName',
    };

    if (!sortBy[0]) return [['paidAt', SortDirection.Desc]];
    const { id, desc } = sortBy[0];

    const direction = desc ? SortDirection.Desc : SortDirection.Asc;
    if (id === i18n.t('salesSummary.orders.note')) {
      return [
        ['totalDiscounts', direction],
        ['refundedSubtotal', direction],
        ['purchasedGiftCardAmount', direction],
      ];
    }
    return sortBy?.map((columnSort) => [columnIdMap[columnSort.id] || columnSort.id, direction]);
  };

  const fetch = useCallback(
    (connectionProps: {
      after?: Maybe<string>;
      before?: Maybe<string>;
      first?: number;
      last?: number;
    }) => {
      setLoading(true);
      if (!connectionProps?.before && !connectionProps?.after) setPageIndex(0);
      apiClient()
        .query<StatsOrdersQuery, StatsOrdersQueryVariables>(StatsOrdersDocument, {
          ...statsQueryVariables,
          ...connectionProps,
          orderBy: Object.fromEntries(sortTableColumns(orderBy, i18n)),
        })
        .toPromise()
        .then((res) => setConnectionData(res.data?.stats.ordersConnection))
        .finally(() => setLoading(false));
    },
    [i18n, statsQueryVariables, orderBy],
  );

  const movePage = useCallback(
    (direction: -1 | 1) => {
      const pageInfo = connectionData?.pageInfo;
      if (!pageInfo) return;
      if (direction === -1) {
        setPageIndex((pageIndex) => pageIndex - 1);
        fetch({ before: pageInfo.startCursor, last: pageSize });
      } else if (direction === 1) {
        setPageIndex((pageIndex) => pageIndex + 1);
        fetch({ after: pageInfo.endCursor, first: pageSize });
      }
    },
    [connectionData?.pageInfo, fetch],
  );

  useEffect(() => fetch({ first: pageSize }), [fetch]);

  const columns = [
    columnHelper.accessor('number', {
      header: i18n.t('salesSummary.orders.orderId'),
      cell: ({ cell, row }) => (
        <LinkNext href={`/orders/${row.original?.id}`}>
          {`${row.original.paymentType === i18n.t('salesSummary.orders.refundUpper') ? '➡️ ' : ''}${cell.getValue()}`}
        </LinkNext>
      ),
    }),
    columnHelper.accessor('paymentType', {
      header: i18n.t('salesSummary.orders.source'),
      enableSorting: false,
      cell: ({ cell, row }) => {
        const value = cell.getValue();
        if (!value) return null;
        if (row.original.streamOrder?.sourceLabel) return row.original.streamOrder.sourceLabel;
        return i18n.t(`paymentTypes.${value}`);
      },
    }),
    columnHelper.accessor('restaurant.name', {
      header: i18n.t('salesSummary.orders.location'),
      size: 10,
    }),
    columnHelper.accessor('netItemSubtotal', {
      ...columnHelpers.currency,
      header: i18n.t('salesSummary.orders.subtotal'),
      size: 10,
    }),
    columnHelper.accessor('tip', {
      ...columnHelpers.currency,
      header: i18n.t('salesSummary.orders.tip'),
      size: 10,
    }),
    columnHelper.accessor('total', {
      ...columnHelpers.currency,
      header: i18n.t('salesSummary.orders.total'),
      size: 10,
    }),
    columnHelper.accessor(
      (row) => {
        const users = uniqBy([row.user].concat(row.paymentUsers), 'id');
        return users.filter((item) => item);
      },
      {
        header: i18n.t('salesSummary.orders.customer'),
        enableSorting: false,
        size: 10,
        cell: ({ cell, row }) => {
          const value = cell.getValue();
          const user = value[0];
          const display =
            value.length > 1
              ? `${value.length} ${i18n.t('salesSummary.orders.customers')}`
              : user?.name || user?.email || (user?.phone && formatPhone(user?.phone)) || '';
          const displayWithLoyalty = row.original.hasLoyaltyTransactions
            ? `${display} ⭐`
            : display;
          return value.length > 1 ? (
            <LinkNext href={`/orders/${row.original.id}`}>{displayWithLoyalty}</LinkNext>
          ) : (
            <LinkNext href={`/customers/${user?.id}`}>{displayWithLoyalty}</LinkNext>
          );
        },
        meta: {
          renderCsv: ({ cell }) =>
            cell
              .getValue()
              .map((user) => (user?.phone && formatPhone(user?.phone)) || user?.name || user?.email)
              .join(', '),
        },
      },
    ),
    columnHelper.accessor(
      (row) =>
        [
          row?.totalDiscountAmount && i18n.t('salesSummary.orders.discount'),
          row?.hasRefund && i18n.t('salesSummary.orders.refund'),
          row?.purchasedGiftCard &&
            i18n.t('salesSummary.orders.purchasedGiftCard', {
              code: row.purchasedGiftCard.code,
            }),
          row?.giftCardTransaction &&
            `${i18n.t('salesSummary.orders.usedGiftCard')} ${
              row.giftCardTransaction.giftCard.code
            }`,
        ]
          .filter((x) => x)
          .join(', '),
      {
        header: i18n.t('salesSummary.orders.note'),
        size: 10,
        cell: ({ cell, row }) => {
          const value = cell.getValue();
          return row.getCanExpand() ? (
            <span onClick={row.getToggleExpandedHandler()}>
              {`${row.getIsExpanded() ? '➖' : '➕'} ${value}`}
            </span>
          ) : (
            value
          );
        },
      },
    ),
    reactionColumn(columnHelper, 'reaction'),
    columnHelper.accessor('paidAt', {
      header: i18n.t('salesSummary.orders.paidAt'),
      size: 10,
      id: 'paidAt',
      cell: ({ cell, row }) =>
        formatDate(cell.getValue(), dateFormats.dateTimeShort, row.original?.restaurant.timeZone),
    }),
  ];

  const getRefundRow = useCallback((order: OrderNode) => {
    return order.refund
      ? [
          {
            id: order.id,
            number: order.number,
            paymentType: 'REFUND',
            paidAt: order.refund.createdAt,
            restaurant: order.restaurant,
            tip: order.refund.tip ? -order.refund.tip : 0,
            netItemSubtotal: order.refund.subtotal ? -order.refund.subtotal : 0,
            total: order.refund.total ? -order.refund.total : 0,
            rowProps: {
              className: refundStyle,
            },
          },
        ]
      : [];
  }, []);

  const handleExport = async (email: string, format?: string) => {
    // Unknown values fall back to CSV rather than throwing. The backend
    // `OrderExportFormatEnum` already rejects unknown formats at the GraphQL
    // boundary, so this fallback exists purely to guard against a future
    // frontend regression (e.g., adding a third `formats` entry without
    // updating this map). Log a warning when it fires so the mismatch
    // (which would otherwise be silent from the user's perspective) shows
    // up in the browser devtools console instead of being swallowed.
    if (format !== undefined && !isExportFormat(format)) {
      console.warn(`[Orders] Unknown export format "${format}", falling back to CSV`);
    }
    const key: ExportFormat = isExportFormat(format) ? format : 'CSV';
    const res = await apiClient()
      .mutation<CreateOrderExportMutation, CreateOrderExportMutationVariables>(
        CreateOrderExportDocument,
        { input: { ...statsQueryVariables, email, format: FORMAT_ENUM_MAP[key] } },
      )
      .toPromise();

    return {
      success: !!res.data?.createOrderExport?.success,
      error: res.error?.message,
    };
  };

  // Both options carry a helper so the modal height stays constant when the
  // user toggles between them (see IME-2641 review — jumping height is bad UX).
  //
  // The memo key includes `storedLocale` because `i18n` is a singleton whose
  // object identity does NOT change when the user switches languages at
  // runtime; only `.locale` is mutated in place. Without `storedLocale` in
  // the dep array the labels/helpers would render in the mount-time language
  // and never refresh on locale change.
  const exportFormats = useMemo(
    () => [
      {
        value: 'CSV',
        label: i18n.t('exportOrder.formatCsv'),
        helper: i18n.t('exportOrder.csvHelper'),
      },
      {
        value: 'QBO',
        label: i18n.t('exportOrder.formatQbo'),
        helper: i18n.t('exportOrder.qboHelper'),
      },
    ],
    [i18n, storedLocale],
  );

  return (
    <ChartSection
      empty={!(connectionData?.nodes && connectionData?.nodes.length > 0)}
      divide
      loading={!connectionData?.nodes && loading}
      Header={
        <ChartHeader
          title={i18n.t('salesSummary.orders.orders')}
          subtitle={[formatTimespan(timespan, i18n), formatTimeRange(times)]
            .filter((x) => x)
            .join('; ')}
        >
          <ExportDataButton onExport={handleExport} formats={exportFormats} />
        </ChartHeader>
      }
    >
      <PagedConnectionTable
        data={connectionData?.nodes?.flatMap((x) => (x ? [x] : []))}
        getSubRows={getRefundRow}
        columns={columns}
        loading={loading}
        pageSize={pageSize}
        pageIndex={pageIndex}
        movePage={movePage}
        sorting={orderBy}
        onSortingChange={setOrderBy}
        pageInfo={connectionData?.pageInfo}
        totalCount={connectionData?.totalCount || 0}
      />
    </ChartSection>
  );
}
